import React, {Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { renderFileSize } from './constants';

import {
  IconButton,
  ListItem,
  Loader,
} from '../base';

import { userHasPermission } from '../../utils/permissions';
import { permissions } from '../../constants/permissions';

import VideoIcon from '@material-ui/icons/Videocam';
import { PdfIcon } from '../../assets/Icons/PdfIcon';
import { AudioIcon } from '../../assets/Icons/AudioIcon';
import { FileIcon } from '../../assets/Icons/FileIcon';
import ImageIcon from '@material-ui/icons/Image';
import DeleteIcon from '@material-ui/icons/Delete';
import MoveIcon from '@material-ui/icons/Redo';
import DownloadIcon from '@material-ui/icons/SaveAlt';
import CreateIcon from '@material-ui/icons/Create';

const styles = theme => ({
  fileListItem: {
    width: 95,
    padding: `${theme.spacing()}px 0`,

    '&:not(:last-child)': {
      marginRight: theme.spacing(2),

      [theme.breakpoints.up('sm')]: {
        marginRight: theme.spacing(4),
      },
    },

    '&.actionMode': {

      [theme.breakpoints.up('md')]: {
        width: '25%',
        maxWidth: 128 + theme.spacing(),
      }
    },
  },
  thumbnailContainer: {
    height: 64,
    width: 64,
    wordBreak: 'break-all',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.text.disabled}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    margin: '0 auto',

    '&.actionMode': {
      height: '100%',
      width: '100%',
    }
  },
  fileSize: {
    textAlign: 'center',
    width: 95,
    margin: `${theme.spacing()}px 0 ${theme.spacing(0.5)}px`,

    '&.actionMode': {
      display: 'none',
    },
  },
  actionsContainer: {
    width: 64,
    display: 'flex',
    justifyContent: 'space-between',

    '&.special': {
      width: '100%',
      justifyContent: 'center',
    },
  },
  specialImage: {
    color: theme.palette.secondary.main,
  },
  nameContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: -10,
  },
  fileName: {
    flex: 1,
    wordBreak: 'break-all',
    maxWidth: 'calc(100vw - 96px)',
  },
  clickableIcon: {
    cursor: 'pointer',
    padding: 0,

    '&:hover': {
      background: 'transparent',
    },
  },
  deleteIcon: {
    color: theme.palette.error.light,
    cursor: 'pointer',
  },
  otherFileContainer: {
    display: 'block',
    padding: `${theme.spacing(1.5)}px 0`,

    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
    }
  },
  otherFileInfoContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',

    '& > *:not(:last-child)': {
      marginRight: theme.spacing(),
    },
  },
  commentP: {
    wordWrap: 'break-word'
  }
});

const renderImagePreview = (file, isActionMode) => {
  const isLandscape = window.screen.width > window.screen.height;
  const size = isActionMode
               && isLandscape
               && window.screen.width > 1023
                ? '128' : '64';

  return (
    file.thumbnailUrl
    ? <img src={file.thumbnailUrl} alt={`${file.name} thumbnail`} height={size} width={size} />
    : <ImageIcon color='secondary'/>
  )
}

const renderOtherFileIcon = file => {
  const type = file.mimeType;

  if (!type) return <Loader color='secondary' />
  if (type && type.startsWith('video')) return <VideoIcon color='secondary' />
  if (type && type.startsWith('image')) return <ImageIcon color='secondary' />
  if (type && type.startsWith('audio')) return <AudioIcon color='secondary' />
  if (type && type.startsWith('application/pdf')) return <PdfIcon color='secondary' />

  return <FileIcon color='secondary' />
}

export const RenderImageFile = withStyles(styles)(
  function RIF({ classes: c, t, gt, file, isSpecial, isActionMode, isAdditional, openModal, openCommentPictureModal, downloadFile, imageOperationsLocked, user }) {
    const actionClass = isActionMode ? 'actionMode' : '';
    const specialClass = isSpecial ? 'special' : '';

    return (
      <ListItem className={classNames(c.fileListItem, actionClass)}>
        <div className={c.fileContainer}>
          {
            file
            ? <Fragment>
                  <div className={classNames(c.thumbnailContainer, actionClass)} onClick={() => openModal('previewFile', file.name)}>
                    {renderImagePreview(file, isActionMode)}
                  </div>
                  { file.size
                    ? <div className={classNames(c.fileSize, actionClass)}>
                        {renderFileSize(file.size)}
                        <p className={c.commentP}>{file.alternativeDescription || gt.noComments}</p>
                      </div> 
                    : null }
                <div className={classNames(c.actionsContainer, specialClass)}>
                  {
                    !isActionMode &&
                    <Fragment>
                      {
                        user && userHasPermission(user, permissions.DOWNLOAD_PICTURES) &&
                        <DownloadIcon className={c.clickableIcon} onClick={() => downloadFile(file.name)} color='secondary' />
                      }
                      <IconButton
                        color='secondary'
                        disabled={imageOperationsLocked}
                        className={c.clickableIcon}
                        onClick={() => openModal('moveFileDialog', file.name)}
                      >
                        <MoveIcon />
                      </IconButton>
                      <DeleteIcon className={c.deleteIcon} onClick={() => openModal('confirmDeleteFile', file.name)} />
                      <IconButton
                        disabled={imageOperationsLocked}
                        className={c.clickableIcon} 
                        onClick={() => openCommentPictureModal('editPictureCommentDialog', file.uuid, file.alternativeDescription)} 
                        color='secondary'
                      >
                        <CreateIcon/>
                      </IconButton>
                    </Fragment>
                  }
                  {
                    isActionMode && isSpecial &&
                    <div className={c.specialImage}>{t.special}</div>
                  }
                </div>
              </Fragment>
            : null
          }
        </div>
      </ListItem>
    )
  }
)

export const RenderOtherFile = withStyles(styles)(
  function ROF({classes: c, gt, file, openModal, downloadFile}) {

    return (
      <ListItem className={c.otherFileContainer}>
        {
          file
          ? <Fragment>
              <div className={c.otherFileInfoContainer}>
                {renderOtherFileIcon(file)}
                <div className={c.fileName}>{file.name || gt.unknown}</div>
              </div>
              <div className={c.otherFileInfoContainer}>
                { file.size ? <div>{renderFileSize(file.size)}</div> : null }
                <DownloadIcon className={c.clickableIcon} onClick={() => downloadFile(file.name)} color='secondary' />
                <DeleteIcon className={c.deleteIcon} onClick={() => openModal('confirmDeleteFile', file.name)} />
              </div>
            </Fragment>
          : null
        }
      </ListItem>
    );
  }
)



// WEBPACK FOOTER //
// ./src/components/FileInput/RenderFile.js